import { GATEWAY_PATH_ROOT, INPUTS_PATH_ROOT } from 'src/routes/config';
import { api, COUNT_URL, SITES_ROOT, SITE_PREVIEWS } from '../api';
import {
  AddNewSiteParams,
  EditParamsTypes,
  EditSitePreviewType,
  GetAllParamsTypes,
  GetByIdParamsType,
} from './types';
import { NewSite } from 'src/features/sites/types/sites';
import { concat } from 'lodash';
import { FormItem } from 'src/features/sites/types/form';

const prepareQueryParams = (params: GetAllParamsTypes) => {
  const skipToValue: string =
    params.skipTo && params.skipTo !== '' ? `&skipTo=${params.skipTo}` : '';
  const sortByValue: string =
    params.sortBy && params.sortBy !== '' ? `&sortBy=${params.sortBy}` : '';
  const descendingValue: string =
    params.descending !== undefined ? `&descending=${params.descending}` : '';
  const searchValue: string =
    params.search && params.search !== '' ? `&search=${params.search}` : '';

  const siteTypeValue: string =
    Array.isArray(params.siteTypes) && params.siteTypes.length > 0
      ? params.siteTypes
          .filter((siteType: string) => siteType !== '' && siteType !== undefined)
          .map((siteType: string) => `&siteType=${siteType}`)
          .join('')
      : '';

  return {
    skipToValue,
    sortByValue,
    descendingValue,
    searchValue,
    siteTypeValue,
  };
};

const getAll = async (params: GetAllParamsTypes) => {
  const preparedParams = prepareQueryParams(params);
  try {
    const response = await api({
      method: 'get',
      url: params.limit
        ? `${SITES_ROOT}/${params.customerId}?limit=${params.limit}${preparedParams.skipToValue}${preparedParams.sortByValue}${preparedParams.descendingValue}${preparedParams.searchValue}${preparedParams.siteTypeValue}`
        : `${SITES_ROOT}/${params.customerId}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const getAssignedForms = async (customerId: string, siteId: string) => {
  const response = await api.get(`/sites/${customerId}/${siteId}/assigned-forms`);

  return response.data;
};

const getAllWithLoop = async (params: GetAllParamsTypes) => {
  let response: NewSite[] = [];
  params.skipTo = '1';
  try {
    let skipTo = 1;
    while (true) {
      params.skipTo = skipTo.toString();
      const data: NewSite[] = await getAll(params);

      if (data.length === 0) {
        break;
      }

      response = concat(response, data);
      skipTo++;
    }

    return response;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const add = async (data: AddNewSiteParams, customerId: string) => {
  try {
    const response = await api({
      method: 'post',
      url: `${SITES_ROOT}/${customerId}`,
      data,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const assignFormToSite = async (forms: FormItem[], customerId: string, siteId: string) =>
  api.patch(`/sites/${customerId}/${siteId}/assigned-forms`, forms);

const getById = async ({ customerId, siteId }: GetByIdParamsType) => {
  try {
    const response = await api({
      method: 'get',
      url: `${SITES_ROOT}/${customerId}/${siteId}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const getCount = async (customerId: string) => {
  try {
    const response = await api({
      method: 'get',
      url: `${SITES_ROOT}/${customerId}${COUNT_URL}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return 0;
  }
};

const edit = async (data: EditParamsTypes, customerId: string, siteId: string) => {
  const response = await api({
    method: 'patch',
    url: `${SITES_ROOT}/${customerId}/${siteId}`,
    data,
  });
  return response.data;
};

const getTags = async (
  customerId: string,
  siteId: string,
  displayName: string | null = null,
  includeLatestValues: boolean = false
) => {
  const queryParams = new URLSearchParams();
  if (displayName) queryParams.append('displayName', displayName);
  queryParams.append('includeLatestValues', includeLatestValues.toString());

  try {
    const response = await api({
      method: 'get',
      url: `${SITES_ROOT}/${customerId}/${siteId}${GATEWAY_PATH_ROOT}${INPUTS_PATH_ROOT}?${queryParams.toString()}`,
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const deleteInput = async (
  customerId: string,
  siteId: string,
  gatewayId: string,
  inputId: string
) => {
  const response = await api({
    method: 'delete',
    url: `${SITES_ROOT}/${customerId}/${siteId}${GATEWAY_PATH_ROOT}/${gatewayId}${INPUTS_PATH_ROOT}/${inputId}`,
  });

  return response.data;
};

const getTagById = async (
  customerId: string,
  siteId: string,
  gatewayId: string,
  inputId: string,
  includeLatestValues: boolean = false
) => {
  const query = `includeLatestValues=${includeLatestValues}`;

  const url = `${SITES_ROOT}/${customerId}/${siteId}${GATEWAY_PATH_ROOT}/${gatewayId}/input/${inputId}?${query}`;

  const response = await api({
    method: 'get',
    url,
  });

  return response.data;
};

const getEditSitePreview = async (customerId: string, siteId: string) => {
  const response = await api({
    method: 'get',
    url: `v2/customers/${customerId}/site/preview?siteId=${siteId}`,
  });
  return response.data;
};

const editSitePreview = async (data: EditSitePreviewType) => {
  const response = await api({
    method: 'put',
    url: `v2/customers/${data.customerId}/site/previews`,
    data,
  });
  return response.data;
};

const getTagsV2 = async (customerId: string, includeLatestValues: boolean = false) => {
  const queryParams = [`includeLatestValues=${includeLatestValues}`].join('&');

  const response = await api({
    method: 'get',
    url: `v2/customers/${customerId}/${SITE_PREVIEWS}?${queryParams}`,
  });

  return response;
};

const toggleMaintenanceMode = async (customerId: string, siteId: string, enabled: boolean) => {
  const response = await api({
    method: 'patch',
    url: `${SITES_ROOT}/${customerId}/sites/${siteId}/maintenance`,
    data: { enabled },
  });
  return response.data;
};

export const SitesService = {
  getAll,
  getAllWithLoop,
  add,
  assignFormToSite,
  getById,
  edit,
  getTags,
  getCount,
  deleteInput,
  getTagById,
  getAssignedForms,
  editSitePreview,
  getEditSitePreview,
  getTagsV2,
  toggleMaintenanceMode,
};
